/**
 * La scheda di un film o di una serie, su un telefono.
 *
 * ## Perché non bastava la schermata del televisore
 *
 * `VodDetailScreen` mette il titolo in caratteri da oltre cento punti accanto a una colonna di
 * dati, con la key art a tutta larghezza dietro. In una colonna da 360 punti quel disegno non si
 * stringe: il titolo scende **una parola per riga**, «Film · 2025» si impila in verticale e la trama
 * diventa una colonna di parole singole. È così che si vedeva sull'emulatore prima di questo file.
 *
 * ## L'ordine è quello della decisione, non quello del televisore
 *
 * Immagine, titolo, i due fatti che contano (tipo e anno), **i tasti**, poi la trama. Su un
 * telefono la trama sta sotto l'azione: chi apre una scheda per guardare non deve scorrere per
 * trovare il tasto, e chi apre per leggere scorre comunque.
 *
 * ## Le stagioni sono una striscia, non una colonna
 *
 * Una serie con dieci stagioni, in verticale, spingerebbe gli episodi fuori dallo schermo. La
 * striscia scorre di lato come le categorie della guida, ed è la stessa forma — un elenco corto di
 * scelte che non deve rubare altezza.
 */

import { useState, type ReactNode } from 'react';
import type { EpisodeInfo, MovieDetail, SeriesDetail, VodItem, WatchProgress } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

export interface PhoneVodDetailScreenProps {
  readonly item: VodItem;
  /** La scheda di una serie: `null` finché non è arrivata. */
  readonly detail: SeriesDetail | null;
  /** La scheda di un film: `null` per una serie, o finché non è arrivata. */
  readonly movie?: MovieDetail | null;
  readonly progress: WatchProgress;
  readonly loadingDetail?: boolean;
  readonly detailErrorCode?: string | null;
  readonly onPlayMovie: (fromStart: boolean) => void;
  readonly onPlayEpisode: (episode: EpisodeInfo, fromStart: boolean) => void;
  readonly inMyList?: boolean;
  readonly onToggleMyList?: () => void;
  readonly onExit: () => void;
}

export function PhoneVodDetailScreen({
  item,
  detail,
  movie = null,
  progress,
  loadingDetail = false,
  detailErrorCode = null,
  onPlayMovie,
  onPlayEpisode,
  inMyList = false,
  onToggleMyList,
  onExit,
}: PhoneVodDetailScreenProps): ReactNode {
  const translator = useTranslator();
  const serie = detail;
  const [stagione, setStagione] = useState<number | null>(null);

  const fatti = serie ?? movie;
  const trama = fatti?.plot ?? '';
  const anno = fatti?.year ?? item.year;
  /*
    La **locandina**, non la key art.

    Il televisore usa `backdropUrl` perché è un'immagine 16:9 su uno schermo 16:9. Qui il riquadro è
    verticale: un 16:9 ritagliato per riempirlo perde metà inquadratura, e su questi cataloghi
    `backdropUrl` spesso **è** una locandina verticale — che riempita in 16:9 si vedeva con due
    bande nere ai lati e il volto tagliato.

    Si preferisce la locandina e si ripiega sulla key art solo se manca: meglio un 16:9 in un
    riquadro verticale che nessuna immagine.
  */
  const immagine = item.posterUrl ?? fatti?.posterUrl ?? fatti?.backdropUrl ?? null;

  /*
    A che punto era rimasto.

    `progress` è l'avanzamento archiviato su questo apparecchio, in **percentuale** e separato fra
    film ed episodi. Se c'è, il tasto principale dice «Riprendi» e quello accanto «Dall'inizio».
    Senza, un solo tasto: due tasti che fanno la stessa cosa chiedono una scelta che non esiste.
  */
  const ripartibile = (progress.movies[item.id] ?? 0) > 0;

  /** La stagione mostrata: quella scelta, o la prima che esiste. */
  const stagioneCorrente =
    serie === null ? null : (serie.seasons.find((s) => s.number === stagione) ?? serie.seasons[0] ?? null);

  return (
    <div
      data-phone-vod-detail="1"
      style={{
        height: '100%',
        overflowY: 'auto',
        background: colors.background,
        color: colors.text,
        paddingBottom: 24,
      }}
    >
      {/*
        La locandina **accanto** al titolo, non sopra a tutta larghezza.

        Una 2:3 a tutta larghezza sarebbe alta una volta e mezza lo schermo: i tasti finirebbero
        sotto il bordo, cioè si nasconderebbe la ragione per cui si è aperta la scheda. Affiancata
        resta intera — niente ritaglio — e il titolo le sta a fianco.
      */}
      {/*
        «Indietro» **sopra** la locandina e non addosso.

        Era posizionato in modo assoluto sull'immagine, che prima occupava tutta la larghezza: con la
        locandina verticale finiva a coprirla proprio nell'angolo dove molte stampano il titolo.
      */}
      <div style={{ padding: '12px 20px 0' }}>
        <button
          type="button"
          data-testid="phone-detail-back"
          onClick={onExit}
          style={{
            minHeight: 40,
            padding: '0 14px',
            borderRadius: 20,
            border: `1px solid ${colors.border}`,
            background: 'transparent',
            color: colors.textMuted,
            font: `600 14px ${FONT_STACK}`,
            cursor: 'pointer',
          }}
        >
          {translator.t('common.back')}
        </button>
      </div>

      <div style={{ padding: '12px 20px 0', display: 'flex', gap: 14 }}>
        <div
          style={{
            flex: 'none',
            width: '38%',
            maxWidth: 160,
            aspectRatio: '2 / 3',
            borderRadius: 12,
            overflow: 'hidden',
            background: colors.panel,
          }}
        >
          {immagine !== null && (
            <img
              src={immagine}
              alt=""
              /*
                `contain` e non `cover`: una locandina è già nelle proporzioni giuste, e ritagliarla
                toglierebbe il titolo stampato sopra — che spesso è l'unico modo di riconoscerla.
              */
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 2 }}>
          <h1
            style={{
              margin: 0,
              font: `800 min(5.5vw, 22px)/1.25 ${FONT_STACK}`,
              letterSpacing: '-.02em',
              // Un titolo che contiene un URL non ha spazi: senza, sfonda la colonna.
              overflowWrap: 'anywhere',
            }}
          >
            {item.title}
          </h1>
          <div style={{ font: `500 13px ${FONT_STACK}`, color: colors.textMuted }}>
            {[
              translator.t(item.kind === 'series' ? 'menu.series' : 'menu.movies'),
              anno > 0 ? String(anno) : null,
              fatti?.genre !== undefined && fatti.genre !== '' ? fatti.genre : null,
            ]
              .filter((pezzo): pezzo is string => pezzo !== null)
              .join(' · ')}
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* I tasti, sopra la trama: chi apre per guardare non deve scorrere per trovarli. */}
        {item.kind !== 'series' && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Azione
              testId="phone-detail-play"
              principale
              label={translator.t(ripartibile ? 'vod.action.resume' : 'vod.action.watch')}
              onClick={() => onPlayMovie(!ripartibile)}
            />
            {ripartibile && (
              <Azione
                testId="phone-detail-from-start"
                label={translator.t('vod.action.fromStart')}
                onClick={() => onPlayMovie(true)}
              />
            )}
            {onToggleMyList !== undefined && (
              <Azione
                testId="phone-detail-mylist"
                label={translator.t(inMyList ? 'vod.action.inMyList' : 'vod.action.addMyList')}
                onClick={onToggleMyList}
              />
            )}
          </div>
        )}

        {trama !== '' && (
          <p style={{ margin: 0, font: `400 15px/1.5 ${FONT_STACK}`, color: colors.textMuted }}>
            {trama}
          </p>
        )}

        {loadingDetail && (
          <p style={{ margin: 0, font: `500 14px ${FONT_STACK}`, color: colors.textFaint }}>
            {translator.t('vod.detail.loading')}
          </p>
        )}

        {detailErrorCode !== null && (
          <p style={{ margin: 0, font: `600 14px ${FONT_STACK}`, color: colors.errorText }}>
            {translator.t('vod.detail.failed')}
          </p>
        )}
      </div>

      {serie !== null && serie.seasons.length === 0 && !loadingDetail && (
        <p style={{ padding: '14px 20px', margin: 0, font: `500 14px ${FONT_STACK}`, color: colors.textFaint }}>
          {translator.t('vod.detail.noEpisodes')}
        </p>
      )}

      {stagioneCorrente !== null && (
        <>
          {/* Le stagioni: una striscia che scorre di lato, come le categorie della guida. */}
          {serie !== null && serie.seasons.length > 1 && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                padding: '16px 20px 0',
                scrollbarWidth: 'none',
              }}
            >
              {serie.seasons.map((s) => {
                const accesa = s.number === stagioneCorrente.number;
                return (
                  <button
                    key={s.number}
                    type="button"
                    data-season={s.number}
                    onClick={() => setStagione(s.number)}
                    aria-pressed={accesa}
                    style={{
                      flex: 'none',
                      minHeight: 40,
                      padding: '0 16px',
                      borderRadius: 20,
                      border: `1px solid ${accesa ? colors.accent : colors.border}`,
                      background: accesa ? colors.accent : 'transparent',
                      color: accesa ? colors.background : colors.textMuted,
                      font: `600 14px ${FONT_STACK}`,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    {`S${s.number}`}
                  </button>
                );
              })}
            </div>
          )}

          <ul style={{ listStyle: 'none', margin: 0, padding: '12px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {stagioneCorrente.episodes.map((episodio) => {
              // L'avanzamento degli episodi è annidato per serie: `episodes[serie][episodio]`.
              const ripreso = (progress.episodes[item.id]?.[episodio.id] ?? 0) > 0;
              return (
                <li key={episodio.id}>
                  <button
                    type="button"
                    data-episode={episodio.id}
                    onClick={() => onPlayEpisode(episodio, !ripreso)}
                    style={{
                      width: '100%',
                      // 56 punti: una riga d'elenco che si colpisce col pollice senza mirare.
                      minHeight: 56,
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${colors.borderFaint}`,
                      background: colors.panel,
                      color: colors.text,
                      font: `500 15px/1.35 ${FONT_STACK}`,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <span>
                      {translator.t('vod.episode', {
                        number: episodio.episode,
                        title: episodio.title,
                      })}
                    </span>
                    <span style={{ font: `500 13px ${FONT_STACK}`, color: colors.textFaint }}>
                      {translator.t(
                        ripreso ? 'vod.action.resumeEpisode' : 'vod.action.startEpisode',
                        { season: episodio.season, episode: episodio.episode },
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

/** Un tasto della scheda. Alto 48: sotto, il dito manca il bersaglio. */
function Azione({
  label,
  onClick,
  principale = false,
  testId,
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly principale?: boolean;
  readonly testId: string;
}): ReactNode {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        minHeight: 48,
        padding: '0 20px',
        borderRadius: 24,
        border: `1px solid ${principale ? colors.accent : colors.border}`,
        background: principale ? colors.accent : 'transparent',
        color: principale ? colors.background : colors.text,
        font: `700 15px ${FONT_STACK}`,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
